import React, { useEffect, useRef, useState } from 'react';
import { relLabel } from '../utils/holeScores';
import '../css/scorecard.css';

export interface CellValue {
  strokes: number | null;
  putts: number | null;
  penalties: number | null;
}

interface CellEditorProps {
  title: string;
  subtitle?: string;
  par: number | null;
  value: CellValue;
  showExtras: boolean;
  hasNext: boolean;
  onSave: (value: CellValue, next: boolean) => void;
  onClose: () => void;
  /** What's wrong with this hole (review flags), shown above the picks. */
  issues?: React.ReactNode;
  /** Label of the "save and go on" button (default "Next hole →"). */
  nextLabel?: string;
  /**
   * Keep part of the screen visible (e.g. the scorecard photo): the sheet
   * opens in the area below `top` / right of `left` (px from the window edge).
   */
  area?: { top?: number; left?: number } | null;
}

const RELS = [-2, -1, 0, 1, 2, 3, 4];
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/**
 * Edit one hole: big quick picks relative to par (shown as strokes), a
 * stepper for anything else, and — when enabled — putts and penalty strokes.
 * A bottom sheet on phones, a small dialog on desktop. Keyboard: digits set
 * the strokes ("1" then "0" = 10), Enter saves and moves to the next hole,
 * Backspace clears, Esc closes.
 */
const CellEditor: React.FC<CellEditorProps> = ({ title, subtitle, par, value, showExtras, hasNext, onSave, onClose, issues, nextLabel = 'Next hole →', area }) => {
  const [strokes, setStrokes] = useState<number | null>(value.strokes);
  const [putts, setPutts] = useState<number | null>(value.putts);
  const [penalties, setPenalties] = useState<number | null>(value.penalties);
  const lastDigit = useRef<{ d: number; at: number } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const current = useRef<CellValue>({ strokes, putts, penalties });
  current.current = { strokes, putts, penalties };

  useEffect(() => { boxRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'Enter') { e.preventDefault(); onSave(current.current, hasNext); return; }
      if (e.key === 'Backspace') { e.preventDefault(); setStrokes(null); return; }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        const d = Number(e.key);
        const prev = lastDigit.current;
        const now = Date.now();
        if (prev && prev.d === 1 && now - prev.at < 900 && d <= 5) {
          setStrokes(10 + d);
          lastDigit.current = null;
        } else if (d > 0) {
          setStrokes(d);
          lastDigit.current = { d, at: now };
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onSave, hasNext]);

  const picks = par != null
    ? RELS.map((rel) => ({ strokes: par + rel, caption: relLabel(rel) })).filter((p) => p.strokes >= 1)
    : [2, 3, 4, 5, 6, 7, 8].map((n) => ({ strokes: n, caption: '' }));

  return (
    <div
      className={`sc-sheet-backdrop${area ? ' sc-sheet-backdrop--area' : ''}`}
      style={area ? { top: area.top ?? 0, left: area.left ?? 0 } : undefined}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sc-sheet" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={boxRef}>
        <p className="sc-sheet-title">{title}</p>
        {subtitle && <p className="sc-sheet-sub">{subtitle}</p>}
        {issues && <div className="sc-sheet-issues">{issues}</div>}

        <div className="sc-picks">
          {picks.map((p) => (
            <button
              key={p.strokes}
              type="button"
              className="sc-pick"
              aria-pressed={strokes === p.strokes}
              onClick={() => setStrokes(p.strokes)}
            >
              <strong>{p.strokes}</strong>
              {p.caption && <span>{p.caption}</span>}
            </button>
          ))}
        </div>

        <div className="sc-stepper-row">
          <label htmlFor="sc-strokes">Strokes</label>
          <div className="sc-stepper">
            <button type="button" aria-label="One less stroke" onClick={() => setStrokes((s) => clamp((s ?? (par ?? 4)) - 1, 1, 25))}>−</button>
            <output id="sc-strokes" aria-live="polite">{strokes ?? '—'}</output>
            <button type="button" aria-label="One more stroke" onClick={() => setStrokes((s) => clamp((s ?? (par ?? 4)) + 1, 1, 25))}>+</button>
          </div>
        </div>

        {showExtras && (
          <>
            <div className="sc-stepper-row">
              <label>Putts</label>
              <div className="sc-chips" role="group" aria-label="Putts">
                {[null, 0, 1, 2, 3, 4].map((n) => (
                  <button key={String(n)} type="button" className="sc-chip" aria-pressed={putts === n} onClick={() => setPutts(n)}>
                    {n == null ? '—' : n}
                  </button>
                ))}
              </div>
            </div>
            <div className="sc-stepper-row">
              <label htmlFor="sc-pen">Penalty strokes</label>
              <div className="sc-stepper">
                <button type="button" aria-label="One less penalty" onClick={() => setPenalties((p) => { const n = (p ?? 0) - 1; return n <= 0 ? null : n; })}>−</button>
                <output id="sc-pen">{penalties ?? 0}</output>
                <button type="button" aria-label="One more penalty" onClick={() => setPenalties((p) => clamp((p ?? 0) + 1, 1, 10))}>+</button>
              </div>
            </div>
          </>
        )}

        <div className="sc-sheet-actions">
          <button type="button" className="sc-btn sc-btn--ghost" onClick={() => setStrokes(null)}>Clear</button>
          <span className="grow" />
          <button type="button" className="sc-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="sc-btn" onClick={() => onSave({ strokes, putts, penalties }, false)}>Save</button>
          {hasNext && (
            <button type="button" className="sc-btn sc-btn--primary" onClick={() => onSave({ strokes, putts, penalties }, true)}>
              {nextLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CellEditor;
